import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Ban, Camera, PackageCheck, Printer, Send, X } from 'lucide-react'
import { api } from '../lib/api'
import { Card, CardContent, CardHeader } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Modal } from '../components/ui/Modal'
import { Badge } from '../components/ui/Badge'
import { usePermissions } from '../contexts/permissions'
import {
  apiError, fmtDate, fmtDateTime, fmtMoney, printVolumeLabels, resizeImage,
  type Location,
} from '../lib/industria'
import {
  PAYABLE_STATUS_LABEL, PURCHASE_STATUS_BADGE, PURCHASE_STATUS_LABEL,
  type PurchaseDetail,
} from '../lib/compras'

const selectClass = 'h-10 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/60'

type ReceiveLine = {
  itemId: string
  code: string
  name: string
  unit: string
  pending: number
  lotControlled: boolean
  qty: string
  perVolume: string
  lotNumber: string
  supplierLot: string
  expiryDate: string
  unitCost: string
}

export function IndustriaCompraDetailPage() {
  const { id = '' } = useParams()
  const queryClient = useQueryClient()
  const { can } = usePermissions()
  const canEdit = can('industria-compras', 'edit')

  const [error, setError] = useState<string | null>(null)
  const [receiveOpen, setReceiveOpen] = useState(false)
  const [lines, setLines] = useState<ReceiveLine[]>([])
  const [head, setHead] = useState({ docNumber: '', docKey: '', docValue: '', dueDate: '', locationId: '', notes: '' })
  const [photos, setPhotos] = useState<string[]>([])
  const [lastVolumes, setLastVolumes] = useState<string[]>([])

  const { data: purchase, isLoading } = useQuery<PurchaseDetail>({
    queryKey: ['compras', 'purchase', id],
    queryFn: async () => (await api.get(`/material-purchases/${id}`)).data,
    enabled: !!id,
  })

  const { data: locations = [] } = useQuery<Location[]>({
    queryKey: ['stock', 'locations'],
    queryFn: async () => (await api.get('/stock/locations')).data,
  })

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['compras'] })
    queryClient.invalidateQueries({ queryKey: ['industria', 'materials'] })
  }

  const orderMutation = useMutation({
    mutationFn: async () => (await api.post(`/material-purchases/${id}/order`, {})).data,
    onSuccess: invalidate,
    onError: (e) => setError(apiError(e, 'Não foi possível enviar o pedido')),
  })

  const cancelMutation = useMutation({
    mutationFn: async (reason: string) =>
      (await api.post(`/material-purchases/${id}/cancel`, { reason })).data,
    onSuccess: invalidate,
    onError: (e) => setError(apiError(e, 'Não foi possível cancelar')),
  })

  const receiveMutation = useMutation({
    mutationFn: async () => (await api.post(`/material-purchases/${id}/receive`, {
      docNumber: head.docNumber.trim() || null,
      docKey: head.docKey.trim() || null,
      docValue: head.docValue ? Number(head.docValue) : null,
      dueDate: head.dueDate || null,
      locationId: head.locationId || null,
      notes: head.notes.trim() || null,
      photos: photos.length ? photos : undefined,
      items: lines
        .filter(l => Number(l.qty) > 0)
        .map(l => {
          const qty = Number(l.qty)
          const per = Number(l.perVolume) > 0 ? Number(l.perVolume) : qty
          const full = Math.floor(qty / per)
          const rest = qty - full * per
          const volumes = [
            ...Array.from({ length: full }, () => ({ quantity: per, locationId: null })),
            ...(rest > 0 ? [{ quantity: rest, locationId: null }] : []),
          ]
          return {
            itemId: l.itemId,
            lotNumber: l.lotNumber.trim() || null,
            supplierLot: l.supplierLot.trim() || null,
            expiryDate: l.expiryDate || null,
            unitCost: l.unitCost ? Number(l.unitCost) : null,
            volumes,
          }
        }),
    })).data as {
      status: string
      received: { lotNumber: string | null; quantity: number; volumes: { id: string; code: string }[] }[]
      payableId: string | null
    },
    onSuccess: (data) => {
      invalidate()
      setReceiveOpen(false)
      setPhotos([])
      setLastVolumes(data.received.flatMap(r => r.volumes.map(v => v.id)))
    },
    onError: (e) => setError(apiError(e, 'Não foi possível registrar o recebimento')),
  })

  function startReceive() {
    if (!purchase) return
    setLines(purchase.items
      .filter(i => i.qtyOrdered > i.qtyReceived)
      .map(i => ({
        itemId: i.id,
        code: i.materialCode,
        name: i.materialName,
        unit: i.unit,
        pending: i.qtyOrdered - i.qtyReceived,
        lotControlled: i.lotControlled,
        qty: String(i.qtyOrdered - i.qtyReceived),
        perVolume: String(i.qtyOrdered - i.qtyReceived),
        lotNumber: '',
        supplierLot: '',
        expiryDate: '',
        unitCost: i.unitCost ? String(Number(i.unitCost)) : '',
      })))
    setHead({
      docNumber: purchase.docNumber ?? '',
      docKey: purchase.docKey ?? '',
      docValue: '',
      dueDate: '',
      locationId: '',
      notes: '',
    })
    setError(null)
    setReceiveOpen(true)
  }

  async function addPhotos(files: FileList) {
    const list = [...files].slice(0, 10 - photos.length)
    const encoded = await Promise.all(list.map(f => resizeImage(f)))
    setPhotos(p => [...p, ...encoded])
  }

  if (isLoading) return <p className="py-16 text-center text-sm text-gray-400">Carregando...</p>
  if (!purchase) return <p className="py-16 text-center text-sm text-gray-400">Pedido não encontrado.</p>

  const pending = purchase.items.filter(i => i.qtyOrdered > i.qtyReceived).length
  const canReceive = ['ordered', 'partial'].includes(purchase.status) && pending > 0
  const receiveTotal = lines.reduce((a, l) => a + Number(l.qty || 0) * Number(l.unitCost || 0), 0)

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link to="/industria/compras" className="mb-1 inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700">
            <ArrowLeft className="h-3 w-3" /> Compras
          </Link>
          <h2 className="font-mono text-xl font-semibold text-gray-900">{purchase.number}</h2>
          <p className="text-sm text-gray-600">
            {purchase.supplierName}
            {purchase.leadTimeDays != null ? ` · ${purchase.leadTimeDays} dias de prazo` : ''}
            {' · '}{purchase.tenantName ?? 'WS (próprio)'}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <Badge variant={PURCHASE_STATUS_BADGE[purchase.status]}>{PURCHASE_STATUS_LABEL[purchase.status]}</Badge>
            <span className="text-sm text-gray-600">{fmtMoney(purchase.totalValue)}</span>
            {purchase.expectedAt && (
              <span className="text-xs text-gray-500">previsto {fmtDate(purchase.expectedAt)}</span>
            )}
            {purchase.docNumber && (
              <span className="rounded-lg bg-gray-100 px-2 py-0.5 font-mono text-xs text-gray-700">
                NF {purchase.docNumber}
              </span>
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {canEdit && purchase.status === 'draft' && (
            <Button loading={orderMutation.isPending} onClick={() => { setError(null); orderMutation.mutate() }}>
              <Send className="h-4 w-4" /> Enviar ao fornecedor
            </Button>
          )}
          {canEdit && canReceive && (
            <Button onClick={startReceive}>
              <PackageCheck className="h-4 w-4" /> Receber
            </Button>
          )}
          {canEdit && purchase.status !== 'received' && purchase.status !== 'cancelled' && (
            <Button
              variant="ghost"
              onClick={() => {
                const reason = window.prompt('Motivo do cancelamento:')
                if (reason) { setError(null); cancelMutation.mutate(reason) }
              }}
            >
              <Ban className="h-4 w-4" /> Cancelar
            </Button>
          )}
        </div>
      </div>

      {error && <p className="rounded-lg bg-red-50 px-4 py-2 text-sm text-red-700">{error}</p>}

      {lastVolumes.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
          <span>{lastVolumes.length} volume(s) etiquetado(s) na última entrada.</span>
          <div className="flex gap-2">
            <Button size="sm" variant="secondary" onClick={() => printVolumeLabels(lastVolumes, 'a4')}>
              <Printer className="h-4 w-4" /> A4
            </Button>
            <Button size="sm" onClick={() => printVolumeLabels(lastVolumes, 'label')}>
              <Printer className="h-4 w-4" /> Etiquetas
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setLastVolumes([])}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <Card>
        <CardHeader><span className="text-sm font-semibold text-gray-900">Itens</span></CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-2.5 text-left font-medium">Material</th>
                  <th className="px-4 py-2.5 text-right font-medium">Pedido</th>
                  <th className="px-4 py-2.5 text-right font-medium">Recebido</th>
                  <th className="px-4 py-2.5 text-right font-medium">Falta</th>
                  <th className="px-4 py-2.5 text-right font-medium">Custo un.</th>
                  <th className="px-4 py-2.5 text-right font-medium">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {purchase.items.map(i => {
                  const missing = Math.max(i.qtyOrdered - i.qtyReceived, 0)
                  return (
                    <tr key={i.id}>
                      <td className="px-4 py-2.5">
                        <span className="text-gray-900">{i.materialName}</span>
                        <span className="block font-mono text-xs text-gray-400">
                          {i.materialCode}{!i.lotControlled ? ' · sem lote' : ''}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono text-gray-900">
                        {i.qtyOrdered.toLocaleString('pt-BR')} <span className="text-xs text-gray-400">{i.unit}</span>
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono text-gray-700">
                        {i.qtyReceived.toLocaleString('pt-BR')}
                      </td>
                      <td className={`px-4 py-2.5 text-right font-mono ${missing ? 'text-amber-700' : 'text-gray-300'}`}>
                        {missing ? missing.toLocaleString('pt-BR') : '—'}
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono text-gray-600">{fmtMoney(i.unitCost)}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-gray-700">
                        {fmtMoney(i.unitCost ? Number(i.unitCost) * i.qtyOrdered : null)}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-5 lg:grid-cols-2">
        <Card>
          <CardHeader><span className="text-sm font-semibold text-gray-900">Lotes recebidos</span></CardHeader>
          <CardContent className="p-0">
            {purchase.lots.length === 0 ? (
              <p className="px-5 py-8 text-center text-sm text-gray-400">Nada recebido ainda</p>
            ) : (
              <ul className="divide-y divide-gray-50">
                {purchase.lots.map(l => (
                  <li key={l.id} className="flex items-center justify-between px-5 py-2.5 text-sm">
                    <div>
                      <span className="text-gray-900">{l.materialName}</span>
                      <span className="block font-mono text-xs text-gray-400">
                        lote {l.lotNumber}
                        {l.expiryDate ? ` · val. ${fmtDate(l.expiryDate)}` : ''}
                        {` · ${fmtDateTime(l.receivedAt)}`}
                      </span>
                    </div>
                    <span className="font-mono text-gray-900">{l.qtyReceived.toLocaleString('pt-BR')}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><span className="text-sm font-semibold text-gray-900">Títulos</span></CardHeader>
          <CardContent className="p-0">
            {purchase.payables.length === 0 ? (
              <p className="px-5 py-8 text-center text-sm text-gray-400">
                Nenhum título — informe o vencimento no recebimento para abrir um
              </p>
            ) : (
              <ul className="divide-y divide-gray-50">
                {purchase.payables.map(p => (
                  <li key={p.id} className="flex items-center justify-between px-5 py-2.5 text-sm">
                    <div>
                      <span className="text-gray-900">{p.description}</span>
                      <span className="block text-xs text-gray-400">
                        {p.dueDate ? `vence ${fmtDate(p.dueDate)}` : 'sem vencimento'}
                        {p.paidAt ? ` · pago ${fmtDate(p.paidAt)}` : ''}
                      </span>
                    </div>
                    <div className="text-right">
                      <span className="block font-mono text-gray-900">{fmtMoney(p.amount)}</span>
                      <Badge variant={p.status === 'paid' ? 'success' : p.status === 'open' ? 'warning' : 'default'}>
                        {PAYABLE_STATUS_LABEL[p.status]}
                      </Badge>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      <Modal title="Receber material do pedido" open={receiveOpen} onClose={() => setReceiveOpen(false)} size="lg">
        <form
          className="space-y-4"
          onSubmit={e => { e.preventDefault(); setError(null); receiveMutation.mutate() }}
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <Input label="Nº da nota" value={head.docNumber} onChange={e => setHead({ ...head, docNumber: e.target.value })} />
            <Input label="Chave da NF-e" value={head.docKey} onChange={e => setHead({ ...head, docKey: e.target.value })} />
            <Input
              label="Valor da nota"
              type="number"
              step="0.01"
              min={0}
              placeholder={receiveTotal ? receiveTotal.toFixed(2) : ''}
              value={head.docValue}
              onChange={e => setHead({ ...head, docValue: e.target.value })}
            />
            <Input
              label="Vencimento (abre o título)"
              type="date"
              value={head.dueDate}
              onChange={e => setHead({ ...head, dueDate: e.target.value })}
            />
            <div className="sm:col-span-2">
              <label className="mb-1 block text-sm font-medium text-gray-700">Localização</label>
              <select className={selectClass} value={head.locationId} onChange={e => setHead({ ...head, locationId: e.target.value })}>
                <option value="">Sem endereço</option>
                {locations.filter(l => l.active).map(l => (
                  <option key={l.id} value={l.id}>{l.code} — {l.zone}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <p className="mb-2 text-sm font-medium text-gray-700">
              O que chegou <span className="font-normal text-gray-500">— cada volume vira uma etiqueta</span>
            </p>
            <div className="space-y-3">
              {lines.map((l, i) => (
                <div key={l.itemId} className="rounded-xl border border-gray-200 p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-sm text-gray-900">{l.name}</span>
                    <span className="font-mono text-xs text-gray-400">
                      {l.code} · falta {l.pending.toLocaleString('pt-BR')} {l.unit}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                    <div>
                      <label className="mb-1 block text-xs text-gray-500">Quantidade</label>
                      <input
                        className="h-9 w-full rounded-lg border border-gray-300 px-2 text-sm"
                        type="number"
                        min={0}
                        value={l.qty}
                        onChange={e => setLines(lines.map((x, j) => j === i ? { ...x, qty: e.target.value } : x))}
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs text-gray-500">Por volume</label>
                      <input
                        className="h-9 w-full rounded-lg border border-gray-300 px-2 text-sm"
                        type="number"
                        min={1}
                        value={l.perVolume}
                        onChange={e => setLines(lines.map((x, j) => j === i ? { ...x, perVolume: e.target.value } : x))}
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs text-gray-500">Custo un.</label>
                      <input
                        className="h-9 w-full rounded-lg border border-gray-300 px-2 text-sm"
                        type="number"
                        step="0.0001"
                        min={0}
                        value={l.unitCost}
                        onChange={e => setLines(lines.map((x, j) => j === i ? { ...x, unitCost: e.target.value } : x))}
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs text-gray-500">Validade</label>
                      <input
                        className="h-9 w-full rounded-lg border border-gray-300 px-2 text-sm"
                        type="date"
                        value={l.expiryDate}
                        onChange={e => setLines(lines.map((x, j) => j === i ? { ...x, expiryDate: e.target.value } : x))}
                      />
                    </div>
                    {l.lotControlled && (
                      <>
                        <div className="col-span-2">
                          <label className="mb-1 block text-xs text-gray-500">Lote (em branco = gerado)</label>
                          <input
                            className="h-9 w-full rounded-lg border border-gray-300 px-2 text-sm"
                            value={l.lotNumber}
                            onChange={e => setLines(lines.map((x, j) => j === i ? { ...x, lotNumber: e.target.value } : x))}
                          />
                        </div>
                        <div className="col-span-2">
                          <label className="mb-1 block text-xs text-gray-500">Lote do fornecedor</label>
                          <input
                            className="h-9 w-full rounded-lg border border-gray-300 px-2 text-sm"
                            value={l.supplierLot}
                            onChange={e => setLines(lines.map((x, j) => j === i ? { ...x, supplierLot: e.target.value } : x))}
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-gray-300 py-3 text-sm text-gray-600 hover:border-blue-300 hover:text-blue-600">
              <Camera className="h-4 w-4" /> Fotos da chegada {photos.length > 0 && `(${photos.length})`}
              <input
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                className="hidden"
                onChange={e => { if (e.target.files) addPhotos(e.target.files); e.target.value = '' }}
              />
            </label>
            {photos.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {photos.map((p, i) => (
                  <div key={i} className="relative">
                    <img src={p} alt="" className="h-14 w-14 rounded-lg object-cover" />
                    <button
                      type="button"
                      className="absolute -right-1 -top-1 rounded-full bg-white p-0.5 shadow ring-1 ring-gray-200"
                      onClick={() => setPhotos(ps => ps.filter((_, j) => j !== i))}
                    >
                      <X className="h-3 w-3 text-gray-500" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <Input label="Observações" value={head.notes} onChange={e => setHead({ ...head, notes: e.target.value })} />

          {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">valor da leva {fmtMoney(receiveTotal)}</span>
            <div className="flex gap-2">
              <Button type="button" variant="secondary" onClick={() => setReceiveOpen(false)}>Cancelar</Button>
              <Button
                type="submit"
                loading={receiveMutation.isPending}
                disabled={lines.filter(l => Number(l.qty) > 0).length === 0}
              >
                Dar entrada
              </Button>
            </div>
          </div>
        </form>
      </Modal>
    </div>
  )
}
